import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSessions } from '@/lib/api/chat';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { QueryState } from '@/components/QueryState';
import { useAgentContext } from '@/features/agents/AgentLayout';
import { useWorkspacePath } from '@/lib/workspacePath';

// Newest-first, bounded page (spec 0012 AC-7): the server hard-caps at 100.
const PAGE_SIZE = 50;

const CHANNEL_LABEL: Record<string, string> = {
  chat: 'Chat',
  email: 'Email',
  slack: 'Slack',
};

function formatDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

/**
 * History tab (spec 0012): the agent's past conversations across all channels,
 * newest first, each opening in the existing chat transcript view (via the
 * ?session= seed). Paginated with Newer/Older; agent-scoped and shared across
 * the tenant's members.
 */
export function AgentHistoryPage() {
  const { agentId, agentRef } = useAgentContext();
  const ws = useWorkspacePath();
  const [offset, setOffset] = useState(0);
  const sessionsQuery = useSessions(agentId, { limit: PAGE_SIZE, offset });
  const sessions = sessionsQuery.data ?? [];

  const hasOlder = sessions.length === PAGE_SIZE;
  const hasNewer = offset > 0;

  return (
    <Card
      title="Conversation history"
      description="Past conversations with this agent across all channels."
    >
      <QueryState isLoading={sessionsQuery.isLoading} error={sessionsQuery.error}>
        {sessions.length === 0 && offset === 0 ? (
          <p data-testid="agent-history-empty" className="py-8 text-sm text-gray-500">
            No conversations yet.
          </p>
        ) : (
          <>
            {sessions.length === 0 ? (
              <p className="py-8 text-sm text-gray-500">No more conversations.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {sessions.map((session) => (
                  <li key={session.id}>
                    <Link
                      to={`${ws('agents', agentRef, 'chat')}?session=${session.id}`}
                      data-testid="agent-history-row"
                      className="flex items-center justify-between gap-3 rounded-md px-2 py-3 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
                    >
                      <span className="min-w-0 truncate text-sm text-gray-900">
                        {session.title || 'Untitled conversation'}
                      </span>
                      <span className="flex shrink-0 items-center gap-3 text-xs text-gray-500">
                        <span
                          data-testid="agent-history-channel"
                          className="rounded bg-gray-100 px-2 py-0.5 font-medium text-gray-600"
                        >
                          {CHANNEL_LABEL[session.origin] ?? session.origin}
                        </span>
                        <span>{formatDate(session.created_at)}</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}

            {(hasOlder || hasNewer) && (
              <div className="mt-4 flex items-center justify-between">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={!hasNewer}
                  onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                  data-testid="agent-history-newer"
                >
                  Newer
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={!hasOlder}
                  onClick={() => setOffset((o) => o + PAGE_SIZE)}
                  data-testid="agent-history-older"
                >
                  Older
                </Button>
              </div>
            )}
          </>
        )}
      </QueryState>
    </Card>
  );
}
